import _ from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {
  CrmToFrontCustomFieldMapping,
  OnUpdateMappedFieldHandler,
} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {MappingRowField} from '../../../../../../../../../src/components/common/mapping/mappingRowField';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {
  Handler,
  HandlerOf,
  Renderer,
  RendererOf,
} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../../../shared/ui/src/components/buttons/iconButton';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {MappingRow} from '../../../common/mapping/mappingRow';
import {FrontFieldDropdownContainer} from './frontFieldDropdownContainer';

/*
 * Props.
 */

type CrmMappingAddFieldDropdownRenderer = (
  popoverProps: PopoverTransferProps,
  crmFieldId?: string,
) => ReactNode;

export interface CrmMappingProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  requiredFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  isDisabled?: boolean;
  isLoading?: boolean;
  isAdmin?: boolean;
  renderHeader: Renderer;
  onUpdateMappedField: OnUpdateMappedFieldHandler;
  onRemoveMappedField: HandlerOf<string>;
  computeFrontTypesFromCrmCustomField: (fieldId: string) => ReadonlyArray<string>;
  computeCrmFieldIcon: (fieldId: string) => IconName | undefined;
  computeFrontFieldIcon: (fieldId: number) => IconName | undefined;
  computeFrontFieldName: (fieldId: number) => string | ReactNode;
  computeCrmFieldName: (fieldId: string) => string | undefined;
  computeCrmFieldType: (fieldId: string) => string | undefined;
  renderAddFieldDropdown: CrmMappingAddFieldDropdownRenderer;
  renderCrmCustomFieldMissingError: Renderer;
  renderCrmPlaceholder: Renderer;
}

/*
 * Style.
 */

const StyledCrmMappingWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 32px 1fr 32px;
`;

const StyledButton = styled(Button)`
  margin-top: 6px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: inline-block;
`;

const StyledDeleteButtonWrapperDiv = styled.div`
  padding: 6px 3px 5px;
`;

/*
 * Component.
 */

export const CrmMapping: FC<CrmMappingProps> = (props) => {
  const {requiredFields, mappedFields, isDisabled, isAdmin, renderHeader, renderAddFieldDropdown} = props;

  return (
    <StyledCrmMappingWrapperDiv>
      {renderHeader()}
      {renderFields(requiredFields, props, true)}
      {renderFields(mappedFields, props)}
      {renderAddFieldButton(Boolean(isAdmin), Boolean(isDisabled), renderAddFieldDropdown)}
    </StyledCrmMappingWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderAddFieldButton(
  isAdmin: boolean,
  isDisabled: boolean,
  renderAddFieldDropdown: RendererOf<PopoverTransferProps>,
) {
  if (!isAdmin) {
    return null;
  }
  return (
    <div>
      <StyledPopoverCoordinator
        isDisabled={isDisabled}
        renderButton={(isModalOpen) => (
          <StyledButton
            size={VisualSizesEnum.MEDIUM}
            variant={ButtonVariantsEnum.LINK}
            isDisabled={isDisabled}
            leftIcon="plusCircleFilledSmall"
            hasHover={isModalOpen}
          >
            <Trans id="crm-syncs-page-mapping-add-fied">Add field</Trans>
          </StyledButton>
        )}
        renderModal={renderAddFieldDropdown}
      />
    </div>
  );
}

function renderFields(
  fields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
  props: CrmMappingProps,
  isReadOnly?: boolean,
) {
  const {
    isDisabled,
    requiredFields,
    mappedFields,
    source,
    isAdmin,
    onRemoveMappedField,
    renderAddFieldDropdown,
    onUpdateMappedField,
    computeFrontTypesFromCrmCustomField,
    renderCrmCustomFieldMissingError,
    renderCrmPlaceholder,
    computeCrmFieldType,
    computeFrontFieldIcon,
  } = props;
  return fields.map((field) => {
    const {
      frontCustomFieldName,
      frontCustomFieldIcon,
      crmCustomFieldName,
      crmCustomFieldIcon,
      isFrontCustomFieldMissing,
      isCrmCustomFieldMissing,
    } = computeMappedFieldDetails(field, props);

    const allSelectedCustomFieldIds = _([...requiredFields, ...mappedFields])
      .map((mappingField) => mappingField.customFieldId)
      .compact()
      .value();

    const onRemove = () => onRemoveMappedField(field.crmFieldId);

    return (
      <MappingRow
        key={field.crmFieldId}
        renderItem={() => maybeRenderDeleteButton(onRemove, !isAdmin || isReadOnly, isDisabled || !isAdmin)}
        renderSourceField={() =>
          renderCrmFieldDropdown(
            field.crmFieldId,
            renderAddFieldDropdown,
            renderCrmCustomFieldMissingError,
            renderCrmPlaceholder,
            crmCustomFieldIcon,
            crmCustomFieldName,
            isReadOnly,
            !isAdmin || isDisabled,
            isCrmCustomFieldMissing,
          )
        }
        renderDestinationField={() => (
          <FrontFieldDropdownContainer
            source={source}
            resourceType={props.resourceType}
            types={computeFrontTypesFromCrmCustomField(field.crmFieldId)}
            selectedFieldId={field.customFieldId}
            selectedCrmFieldType={computeCrmFieldType(field.crmFieldId)}
            selectedFieldIcon={frontCustomFieldIcon}
            frontCustomFieldName={frontCustomFieldName}
            crmCustomFieldName={crmCustomFieldName}
            computeFrontFieldIcon={computeFrontFieldIcon}
            isReadOnly={isReadOnly}
            isDisabled={!isAdmin || isDisabled}
            allSelectedFieldIds={allSelectedCustomFieldIds}
            onSelect={(customFieldId) => onUpdateMappedField(field.crmFieldId, customFieldId)}
            isFrontCustomFieldMissing={isFrontCustomFieldMissing}
          />
        )}
      />
    );
  });
}

function renderCrmFieldDropdown(
  crmFieldId: string,
  renderAddFieldDropdown: CrmMappingAddFieldDropdownRenderer,
  renderCrmCustomFieldMissingError: Renderer,
  renderCrmPlaceholder: Renderer,
  crmCustomFieldIcon?: IconName,
  crmCustomFieldName?: string,
  isReadOnly?: boolean,
  isDisabled?: boolean,
  isCrmCustomFieldMissing?: boolean,
) {
  return (
    <PopoverCoordinator
      isDisabled={isReadOnly || isDisabled}
      renderButton={() => (
        <MappingRowField
          iconName={crmCustomFieldIcon}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          hasError={isCrmCustomFieldMissing}
          errorMessage={renderCrmCustomFieldMissingError()}
        >
          {crmCustomFieldName ?? renderCrmPlaceholder()}
        </MappingRowField>
      )}
      renderModal={(popoverProps) => renderAddFieldDropdown(popoverProps, crmFieldId)}
    />
  );
}

function computeMappedFieldDetails(field: CrmToFrontCustomFieldMapping, props: CrmMappingProps) {
  const {computeFrontFieldName, computeFrontFieldIcon, computeCrmFieldName, computeCrmFieldIcon} = props;
  let isCrmCustomFieldMissing = false;
  let isFrontCustomFieldMissing = false;

  // Compute crm information.
  const crmCustomFieldName = computeCrmFieldName(field.crmFieldId);
  const crmCustomFieldIcon = computeCrmFieldIcon(field.crmFieldId);
  // If we fail to find crm custom field information, return early and do not render.
  if (!crmCustomFieldName || !crmCustomFieldIcon) {
    isCrmCustomFieldMissing = true;
  }

  if (!field.customFieldId) {
    return {
      crmCustomFieldName,
      crmCustomFieldIcon,
      isCrmCustomFieldMissing,
    };
  }

  const frontCustomFieldName = computeFrontFieldName(field.customFieldId);
  const frontCustomFieldIcon = computeFrontFieldIcon(field.customFieldId);
  // If we fail to find custom field information, return early and do not render.
  if (!frontCustomFieldName || !frontCustomFieldIcon) {
    isFrontCustomFieldMissing = true;
  }

  return {
    frontCustomFieldName,
    frontCustomFieldIcon,
    crmCustomFieldName,
    crmCustomFieldIcon,
    isCrmCustomFieldMissing,
    isFrontCustomFieldMissing,
  };
}

function maybeRenderDeleteButton(onRemove?: Handler, isReadOnly?: boolean, isDisabled?: boolean) {
  if (isReadOnly || !onRemove) {
    return <div />;
  }
  return (
    <StyledDeleteButtonWrapperDiv>
      <IconButton iconName="closeSmall" label={null} onClick={onRemove} isDisabled={isDisabled} />
    </StyledDeleteButtonWrapperDiv>
  );
}
